/**
 * Error Formatter Utility
 * Converts API validation errors into user-friendly messages
 */

/**
 * Field name mapping - converts technical field names to user-friendly labels
 */
const FIELD_LABELS = {
  // Club fields
  name: 'Club Name',
  slug: 'Club Slug',
  club_type: 'Club Type',
  description: 'Description',
  address: 'Address',
  city: 'City',
  state: 'State',
  zip_code: 'ZIP Code',
  country: 'Country',
  phone: 'Phone Number',
  email: 'Email Address',
  website: 'Website',
  timezone: 'Timezone',
  currency: 'Currency',
  payment_terms: 'Payment Terms',
  
  // Admin fields
  admin_first_name: 'Admin First Name',
  admin_last_name: 'Admin Last Name',
  admin_email: 'Admin Email',
  admin_phone: 'Admin Phone',
  admin_password: 'Admin Password',
  
  // Player fields
  first_name: 'First Name',
  last_name: 'Last Name',
  date_of_birth: 'Date of Birth',
  
  // Common fields
  title: 'Title',
  content: 'Content',
  status: 'Status',
  start_date: 'Start Date',
  end_date: 'End Date'
};

/**
 * Validation message improvements - makes technical messages more user-friendly
 */
const MESSAGE_IMPROVEMENTS = {
  'String should have at least': 'must be at least',
  'String should have at most': 'must be at most',
  'value is not a valid email address': 'must be a valid email address',
  'field required': 'is required',
  'Input should be a valid': 'must be a valid',
  'ensure this value': 'must',
  'value_error': 'is invalid'
};

/**
 * Parse Pydantic validation error location
 * Converts ['body', 'admin_first_name'] to 'admin_first_name'
 */
const parseFieldLocation = (loc) => {
  if (!Array.isArray(loc)) return 'form';
  
  // Remove 'body' prefix if present
  const filtered = loc.filter(item => item !== 'body');
  return filtered.join('.');
};

/**
 * Get user-friendly field label
 */
const getFieldLabel = (fieldName) => {
  return FIELD_LABELS[fieldName] || fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Improve validation message
 */
const improveMessage = (message) => {
  let improved = message;
  
  // Apply message improvements
  Object.entries(MESSAGE_IMPROVEMENTS).forEach(([pattern, replacement]) => {
    improved = improved.replace(new RegExp(pattern, 'gi'), replacement);
  });
  
  // Capitalize first letter
  improved = improved.charAt(0).toUpperCase() + improved.slice(1);
  
  return improved;
};

/**
 * Format a single validation error
 * @param {Object} error - Pydantic validation error object
 * @returns {string} User-friendly error message
 */
export const formatValidationError = (error) => {
  const fieldName = parseFieldLocation(error.loc);
  const fieldLabel = getFieldLabel(fieldName);
  const message = improveMessage(error.msg);
  
  return `${fieldLabel} ${message}`;
};

/**
 * Format API error response into user-friendly messages
 * @param {Error|Object} error - Error object or API response
 * @returns {Array<string>} Array of user-friendly error messages
 */
export const formatApiError = (error) => {
  // If it's a simple Error object with a message
  if (error instanceof Error) {
    const message = error.message;
    
    // Try to parse if it looks like a Pydantic error string
    if (message.includes('body.') || message.includes(':')) {
      return parseErrorString(message);
    }
    
    return [message];
  }
  
  // If it's an API response with detail array (Pydantic validation errors)
  if (error.detail && Array.isArray(error.detail)) {
    return error.detail.map(formatValidationError);
  }
  
  // If it's an API response with detail string
  if (error.detail && typeof error.detail === 'string') {
    return [error.detail];
  }
  
  // If it's an API response with message
  if (error.message) {
    return [error.message];
  }
  
  // Fallback
  return ['An unexpected error occurred. Please try again.'];
};

/**
 * Parse error string that contains multiple validation errors
 * Example: "body.admin_first_name: String should have at least 2 characters, body.admin_last_name: ..."
 */
const parseErrorString = (errorString) => {
  // Split by comma to get individual errors
  const parts = errorString.split(',').map(s => s.trim());
  
  return parts.map(part => {
    // Try to extract field and message
    const match = part.match(/^(?:body\.)?([^:]+):\s*(.+)$/);
    
    if (match) {
      const [, fieldName, message] = match;
      const fieldLabel = getFieldLabel(fieldName);
      const improvedMessage = improveMessage(message);
      return `${fieldLabel} ${improvedMessage}`;
    }
    
    // If no match, return as is but improved
    return improveMessage(part);
  });
};

/**
 * Extract field-specific errors from error array
 * Useful for inline field validation display
 * @param {Array<string>} errors - Array of error messages
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const extractFieldErrors = (errors) => {
  const fieldErrors = {};
  
  errors.forEach(error => {
    // Try to extract field name from error message
    Object.entries(FIELD_LABELS).forEach(([fieldName, label]) => {
      if (error.startsWith(label)) {
        fieldErrors[fieldName] = error.replace(label, '').trim();
      }
    });
  });
  
  return fieldErrors;
};

